import { interpolate, Easing } from 'remotion';
import { CSSProperties } from 'react';

// Utils
import { combineClasses } from '@/utils/className';
import { getPluralWord } from '@/utils/text';

// Constants
import { FLIP_COUNTER_CONFIG, FLIP_SHADOW_OPACITY } from './constants';

interface FlipNumberProps {
  value: number;
  nextValue: number;
  progress: number;
  unit?: string;
  className?: string;
  fontSize?: number;
  textStyle?: CSSProperties;
  bgStyle?: CSSProperties;
}

/**
 * Mechanical flip display with 3D animation (like airport departure boards)
 */
export const FlipNumber = ({
  value,
  nextValue,
  progress,
  unit,
  className,
  fontSize = FLIP_COUNTER_CONFIG.DEFAULT_FONT_SIZE,
  textStyle,
  bgStyle,
}: FlipNumberProps) => {
  // Top half rotates from 0° to -90° (first half of flip)
  const topFlipRotation = interpolate(progress, [0, 0.5], [0, -90], {
    extrapolateRight: 'clamp',
    easing: Easing.inOut(Easing.ease),
  });

  // Bottom half rotates from 90° to 0° (second half of flip)
  const bottomFlipRotation = interpolate(progress, [0.5, 1], [90, 0], {
    extrapolateLeft: 'clamp',
    easing: Easing.inOut(Easing.ease),
  });

  // Shadow gradually appears and disappears during flip for depth effect
  const topShadowOpacity = interpolate(
    progress,
    [0, 0.5],
    [0, FLIP_SHADOW_OPACITY],
    { extrapolateRight: 'clamp' },
  );

  const bottomShadowOpacity = interpolate(
    progress,
    [0.5, 1],
    [FLIP_SHADOW_OPACITY, 0],
    { extrapolateLeft: 'clamp' },
  );

  // Content of the card face (Number + Label)
  const renderCardContent = (val: number) => (
    <div
      className={combineClasses(
        'size-full flex-center flex-col shadow-2xl',
        className,
      )}
      style={bgStyle}
    >
      <span
        className="font-bold leading-tight tabular-nums"
        style={{ fontSize, ...textStyle }}
      >
        {String(val)}
      </span>
      {unit && (
        <span
          className="tracking-widest mt-2 font-bold text-2xl"
          style={textStyle}
        >
          {getPluralWord(val, unit)}
        </span>
      )}
    </div>
  );

  const renderStaticHalf = (top: string, displayValue: number) => (
    <div className="half-card" style={{ top }}>
      <div
        className="absolute w-full h-[200%]"
        style={{ top: top === '0' ? 0 : '-100%' }}
      >
        {renderCardContent(displayValue)}
      </div>
    </div>
  );

  const renderFlippingHalf = (
    top: string,
    rotation: number,
    shadowOpacity: number,
    displayValue: number,
    zIndex: number,
  ) => (
    <div
      className="half-card backface-hidden"
      style={{
        top,
        transformOrigin: top === '0' ? 'bottom center' : 'top center',
        transform: `rotateX(${rotation}deg)`,
        zIndex,
      }}
    >
      <div
        className="absolute w-full h-[200%]"
        style={{ top: top === '0' ? 0 : '-100%' }}
      >
        {renderCardContent(displayValue)}
      </div>
      <div
        className="absolute inset-0 bg-black pointer-events-none"
        style={{ opacity: shadowOpacity }}
      />
    </div>
  );

  return (
    <div className="relative size-full perspective-1000 preserve-3d">
      {/* Static layers */}
      {renderStaticHalf('0', nextValue)}
      {renderStaticHalf('50%', value)}

      {/* Flipping layers */}
      {renderFlippingHalf('0', topFlipRotation, topShadowOpacity, value, 2)}
      {renderFlippingHalf(
        '50%',
        bottomFlipRotation,
        bottomShadowOpacity,
        nextValue,
        3,
      )}

      {/* Center hinge line */}
      <div
        className={combineClasses(
          'absolute top-1/2 left-0 right-0 h-0.5 bg-black opacity-20 z-10 pointer-events-none',
          !!bgStyle && 'hidden',
        )}
        style={{ transform: 'translateY(-0.5px)' }}
      />

      {/* Hinge highlight */}
      <div
        className="absolute top-1/2 left-0 right-0 h-px bg-white opacity-10 z-20 pointer-events-none"
        style={{ transform: 'translateY(-0.5px)' }}
      />
    </div>
  );
};
